import { Head, router } from "@inertiajs/react"
import { Badge, Button, Card, Code, DataList, Flex, Heading, Table, Text } from "@radix-ui/themes"
import { Check, X } from "lucide-react"
import { z } from "zod"

import { formatDateTime } from "@/lib/datetime"
import ProjectLayout from "@/layouts/project-layout"
import {
  projectCustomersPath,
  projectDashboardPath,
  sendTestPushProjectCustomerPath,
  toggleSubscriptionProjectCustomerPath,
} from "@/routes"
import {
  CustomerDeviceSchema,
  CustomerSchema,
  EventSchema,
  ProjectSchema,
  SegmentSchema,
} from "@/schemas"

const PropsSchema = z.object({
  project: ProjectSchema,
  customer: CustomerSchema,
  customer_devices: z.array(CustomerDeviceSchema),
  customer_segments: z.array(SegmentSchema),
  recent_events: z.array(EventSchema),
})

type Props = z.infer<typeof PropsSchema>

export default function CustomerShow(props: Props) {
  const validatedProps = PropsSchema.parse(props)
  const { project, customer, customer_devices, customer_segments, recent_events } = validatedProps

  const handleToggleSubscription = (type: "campaigns" | "newsletters") => {
    router.patch(toggleSubscriptionProjectCustomerPath(project.slug, customer.id), { type })
  }

  const handleSendTestPush = () => {
    router.post(sendTestPushProjectCustomerPath(project.slug, customer.id))
  }

  return (
    <ProjectLayout
      projectId={project.slug}
      breadcrumbs={[
        { title: project.name, href: projectDashboardPath(project.slug) },
        { title: "Customers", href: projectCustomersPath(project.slug) },
        { title: customer.email },
      ]}
    >
      <Head title={`Customer: ${customer.email}`} />

      <Flex direction="column" gap="6" p="6">
        <Flex justify="between" align="center">
          <Heading size="6" weight="medium">
            {customer.email}
          </Heading>
          <Badge color="blue" variant="soft">
            {customer.events_count} events
          </Badge>
        </Flex>

        <Card>
          <Flex direction="column" gap="4" p="4">
            <Heading size="4" weight="medium">
              Customer information
            </Heading>
            <DataList.Root>
              <DataList.Item>
                <DataList.Label>Email</DataList.Label>
                <DataList.Value>{customer.email}</DataList.Value>
              </DataList.Item>
              {customer.name && (
                <DataList.Item>
                  <DataList.Label>Name</DataList.Label>
                  <DataList.Value>{customer.name}</DataList.Value>
                </DataList.Item>
              )}
              <DataList.Item>
                <DataList.Label>Events count</DataList.Label>
                <DataList.Value>{customer.events_count}</DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Created</DataList.Label>
                <DataList.Value>
                  {new Date(customer.created_at).toLocaleDateString()}
                </DataList.Value>
              </DataList.Item>
              <DataList.Item>
                <DataList.Label>Subscriptions</DataList.Label>
                <DataList.Value>
                  <Flex gap="1">
                    <Badge
                      color={customer.wants_campaigns ? "green" : "red"}
                      variant="soft"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleToggleSubscription("campaigns")}
                    >
                      <Flex align="center" gap="1">
                        {customer.wants_campaigns ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        Campaigns
                      </Flex>
                    </Badge>
                    <Badge
                      color={customer.wants_newsletters ? "green" : "red"}
                      variant="soft"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleToggleSubscription("newsletters")}
                    >
                      <Flex align="center" gap="1">
                        {customer.wants_newsletters ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                        Newsletters
                      </Flex>
                    </Badge>
                  </Flex>
                </DataList.Value>
              </DataList.Item>
            </DataList.Root>
          </Flex>
        </Card>

        {customer.custom_fields && Object.keys(customer.custom_fields).length > 0 && (
          <Card>
            <Flex direction="column" gap="4" p="4">
              <Heading size="4" weight="medium">
                Custom fields
              </Heading>
              <DataList.Root>
                {Object.entries(customer.custom_fields).map(([key, value]) => (
                  <DataList.Item key={key}>
                    <DataList.Label>{key}</DataList.Label>
                    <DataList.Value>
                      {typeof value === "object" ? JSON.stringify(value) : String(value)}
                    </DataList.Value>
                  </DataList.Item>
                ))}
              </DataList.Root>
            </Flex>
          </Card>
        )}

        <Card>
          <Flex direction="column" gap="4" p="4">
            <Flex justify="between" align="center">
              <Heading size="4" weight="medium">
                Devices
              </Heading>
              <Flex align="center" gap="2">
                <Badge color="gray" variant="soft">
                  {customer_devices.length} total
                </Badge>
                <Button
                  size="1"
                  variant="soft"
                  onClick={handleSendTestPush}
                  disabled={customer_devices.length === 0}
                >
                  Send test push
                </Button>
              </Flex>
            </Flex>

            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>Token</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>Updated</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {customer_devices.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <Text color="gray" size="2">
                        no devices registered
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  customer_devices.map((device) => (
                    <Table.Row key={device.id}>
                      <Table.Cell style={{ maxWidth: 520 }}>
                        <Code style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                          {device.apns_token}
                        </Code>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge color={device.is_active ? "green" : "red"} variant="soft">
                          {device.is_active ? "active" : "inactive"}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>{formatDateTime(device.updated_at)}</Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="4" p="4">
            <Heading size="4" weight="medium">
              Segments
            </Heading>
            {customer_segments.length > 0 ? (
              <Flex wrap="wrap" gap="2">
                {customer_segments.map((segment) => (
                  <Badge key={segment.id} variant="soft" color="blue">
                    {segment.name}
                  </Badge>
                ))}
              </Flex>
            ) : (
              <p>This customer is not a member of any segments.</p>
            )}
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="4" p="4">
            <Flex justify="between" align="center">
              <Heading size="4" weight="medium">
                recent events
              </Heading>
              <Badge color="gray" variant="soft">
                {recent_events.length} shown
              </Badge>
            </Flex>

            <Table.Root>
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeaderCell>event</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>created</Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell>payload</Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {recent_events.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={3}>
                      <Text color="gray" size="2">
                        no events for this customer yet
                      </Text>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  recent_events.map((event) => (
                    <Table.Row key={event.id}>
                      <Table.Cell>
                        <Flex align="center" gap="2" wrap="wrap">
                          <Code>{event.name}</Code>
                          {event.is_test && (
                            <Badge color="orange" variant="soft">
                              test
                            </Badge>
                          )}
                        </Flex>
                      </Table.Cell>
                      <Table.Cell>{formatDateTime(event.created_at)}</Table.Cell>
                      <Table.Cell style={{ maxWidth: 520 }}>
                        {event.custom_fields ? (
                          <Code style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {JSON.stringify(event.custom_fields)}
                          </Code>
                        ) : (
                          <Text color="gray" size="2">
                            -
                          </Text>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Flex>
        </Card>
      </Flex>
    </ProjectLayout>
  )
}
